import { Box, CircularProgress, Grid } from '@mui/material';
import { FormikHelpers } from 'formik';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import useSWR from 'swr';

import { queryParams, withQueryParams } from '@shared/commonFunctions';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';
import { useConfirmationModal } from '@shared/hooks/useConfirmationModal';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { CancelButton } from '@shared/inputs/Buttons';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';

import { CardForm } from './components/CardForm';
import { UpdateCardModal } from './components/UpdateCardModal/UpdateCardModal';
import { requestAddCopyPayment, requestAddNewPayment, requestCancelPayment, requestProcessPayment, requestUpdateCard } from './requests';
import { Card, CreditCardPaymentProcessStatusEnum, ICardFormData, ICardsResponse, IConfirmationProcessData, ICopyCard, IUpdateCardFormData } from './types';

const NEW_CARD_CONTAINER_ID = 'new-card-container';

interface IDownPaymentModalProps {
  handleClose(): void;
  initialDownPayment: number;
}

export const DownPaymentModal = ({ handleClose, initialDownPayment }: IDownPaymentModalProps) => {
  const {
    openDialog,
    isOpen: isConirmModalOpen,
    onConfirm,
    onCancel: onCancelConfirmModal
  } = useConfirmationModal();
  const { userId = '' } = useUserAccount();
  const { id: contactId, dealId } = queryParams();
  const dispatch = useDispatch();

  const [copiedCard, setCopiedCard] = useState<Card | null>(null);
  const [cardToUpdate, setCardToUpdate] = useState<Card | null>(null);
  const [confirmationData, setConfirmationData] = useState<IConfirmationProcessData | null>(null);
  const [updateCardModalPosition, setUpdateCardModalPosition] = useState(0);
  const [cancelBtnLoading, setCancelBtnLoading] = useState(false);

  const { data: savedCards, mutate: reloadCards, error: savedCardsError } = useSWR<ICardsResponse>(
    dealId && contactId && withQueryParams(`/${SERVICES.PAYMENT}/deal/${dealId}/cards`, {
      contactId,
    }, false),
  );

  const isLoading = !savedCards && !savedCardsError;

  const handleSubmitSaveCard = useCallback(
    async (data: ICardFormData, { resetForm }: FormikHelpers<ICardFormData>) => {
      const payload: ICopyCard = {
        contactId,
        dealId,
        contactCreditCardId: copiedCard?.contactCreditCardId ?? '',
        isActive: copiedCard?.isActive ?? false,
        amount: data?.amount ?? 0,
        notes: data.notes ?? '',
        processingDate: data?.processingDate ?? null,
        downPaymentOnDeal: initialDownPayment,
        paymentType: data?.paymentType ?? null,
      };

      const response = copiedCard?.isCopied
        ? await requestAddCopyPayment(payload)
        : await requestAddNewPayment(contactId, dealId, data);

      if (response) {
        await reloadCards();
        copiedCard && setCopiedCard(null);
        resetForm();
      }
    },
    [contactId, dealId, copiedCard, initialDownPayment]
  );

  const handleCancelPayment = useCallback(
    async (downPaymentId: string) => {
      setCancelBtnLoading(true);
      const response = await requestCancelPayment(contactId, dealId, downPaymentId);

      if (response) {
        reloadCards();
      }
      setCancelBtnLoading(false);
      copiedCard && setCopiedCard(null);
    },
    [contactId, dealId]
  );

  const handleUpdateCard = useCallback(
    async (formData: IUpdateCardFormData) => {
      if (cardToUpdate) {
        const data: ICardFormData = {
          ...cardToUpdate,
          ...formData,
        };

        const response = await requestUpdateCard(contactId, dealId, userId, data);

        if (response) {
          await reloadCards();
          setCardToUpdate(null);
          setUpdateCardModalPosition(0);
        }
      }
    },
    [contactId, dealId, userId, cardToUpdate]
  );

  const handleProcessPayment = useCallback(
    async (data: ICardFormData, { resetForm }: FormikHelpers<ICardFormData>) => {
      setConfirmationData({
        amount: data.amount,
        cardNumber: `**** **** **** ${data.cardNumber.split(' ')?.[3]}`,
        holderName: data.holderName
      });

      const result = await openDialog();

      if (result) {
        let saveResponse;

        if (!data.downPaymentId) {
          saveResponse = await requestAddNewPayment(contactId, dealId, data);
          data.downPaymentId = saveResponse?.data.downPaymentId;
        }

        const response = await requestProcessPayment(contactId, dealId, userId, data);

        if (response) {
          await reloadCards();
          resetForm();
        }
      }
      dispatch(setLastCommentsRefresh(new Date().toISOString()));

      setConfirmationData(null);
    },
    [userId, contactId, dealId]
  );

  const handleCopyCard = useCallback(
    (copiedCardData: Card) => {
      const addNewCardContainer = document.getElementById(NEW_CARD_CONTAINER_ID);

      setCopiedCard({
        ...copiedCardData,
        isCopied: true,
        processingStatus: CreditCardPaymentProcessStatusEnum.PENDING,
        processingDate: new Date(),
        amount: null,
        notes: '',
        downPaymentId: '',
      });

      if (addNewCardContainer) {
        scrollIntoView(addNewCardContainer, {
          behavior: 'smooth',
          scrollMode: 'always',
          block: 'start',
        });
      }
    },
    []
  );

  const handleCloseUpdateCard = useCallback(() => {
    setCardToUpdate(null);
    setUpdateCardModalPosition(0);
  }, []);

  const handleFormUpdateCard = useCallback((cardData: Card) => {
    setCardToUpdate(cardData);
    setUpdateCardModalPosition(prevState => prevState + 1);
  }, []);

  return (
    <Modal
      open
      title="Down payment form"
      dialogWidth="1000px"
      dialogMinHeight='500px'
      onClose={handleClose}
    >
      {isConirmModalOpen && (
        <AlertModal
          open
          title="Process Payment"
          subtitle={`Process ${confirmationData?.holderName} ${confirmationData?.cardNumber} for ${confirmationData?.amount} dollars?`}
          onClose={onCancelConfirmModal}
          onConfirm={onConfirm}
        />
      )}

      {cardToUpdate && (
        <UpdateCardModal
          updateCardModalPosition={updateCardModalPosition}
          handleSubmit={handleUpdateCard}
          handleClose={handleCloseUpdateCard}
        />
      )}

      {isLoading || cancelBtnLoading ? (
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '500px' }}>
          <Grid item>
            <CircularProgress size={50} />
          </Grid>
        </Grid>
      ) : (
        <>
          <>
            <Box height="auto">
              <Grid container>
                <Grid item md={12}>
                  {savedCards?.getCardModels?.map((cardData) => (
                    <CardForm
                      key={cardData.index}
                      isSaved
                      paymentIndex={cardData.index}
                      data={cardData}
                      initialDownPayment={initialDownPayment}
                      handleCopyCard={() => handleCopyCard(cardData)}
                      handleSubmitSaveCard={handleSubmitSaveCard}
                      handleProcessPayment={handleProcessPayment}
                      handleCancelPayment={() => handleCancelPayment(cardData.downPaymentId)}
                      handleUpdateCard={() => handleFormUpdateCard(cardData)}
                    />
                  ))}

                  <div id={NEW_CARD_CONTAINER_ID}>
                    <CardForm
                      data={copiedCard}
                      initialDownPayment={copiedCard?.isCopied ? 0 : initialDownPayment}
                      handleSubmitSaveCard={handleSubmitSaveCard}
                      handleProcessPayment={handleProcessPayment}
                    />
                  </div>
                </Grid>
              </Grid>
            </Box>
          </>

          <Box display="flex" justifyContent="flex-end" padding={1}>
            <CancelButton onClick={handleClose} />
          </Box>
        </>
      )}
    </Modal>
  );
};
